<template>
  <div
    class="flex flex-col justify-center items-center gap-y-4 sm:gap-y-0 sm:flex-row sm:items-start sm:justify-start sm:gap-x-4"
  >
    <!-- Time Selector -->
    <OnClickOutside @trigger="isDateRangeModalOpen = false" class="relative sm:self-stretch">
      <SlotButton
        id="dateFilterToggleButton"
        @click="isDateRangeModalOpen = !isDateRangeModalOpen"
        classes="flex items-center justify-start gap-x-4 h-11 sm:h-full"
        alt
      >
        <span>{{ dateFilterButtonText }}</span>
        <ChevronDown class="w-4 h-4" />
      </SlotButton>
      <div
        v-show="isDateRangeModalOpen"
        id="dateFilterModal"
        class="absolute -bottom-2 left-1/2 -translate-x-1/2 max-h-600px overflow-auto translate-y-full bg-surface-primary shadow-podHover rounded border border-linear-subtle pt-6 z-20 sm:left-0 sm:translate-x-0 max-w-[90vw] sm:max-w-none"
      >
        <div class="flex flex-col space-y-4 px-6 md:flex-row md:space-y-0 md:space-x-8">
          <div>
            <span class="font-label text-sm uppercase">Start Date</span>
            <DatePicker
              id="startDatePicker"
              class="border border-linear-subtle shadow-none"
              :selectedDate="startDate"
              @datePickerChange="startDate = $event.date"
            />
          </div>
          <div>
            <span class="font-label text-sm uppercase">End Date</span>
            <DatePicker
              id="endDatePicker"
              class="border border-linear-subtle shadow-none"
              :selectedDate="endDate"
              @datePickerChange="endDate = $event.date"
            />
          </div>
        </div>
        <div
          v-if="dateRangeErrorMessage"
          id="dateRangeErrorMessage"
          class="px-6 mt-4 mb-4 text-alerts-warning md:mb-0"
        >
          {{ dateRangeErrorMessage }}
        </div>
        <div
          class="mt-4 py-4 px-6 border-t border-linear-subtle flex items-center justify-start sticky bottom-0 bg-surface-primary md:border-none md:mt-0"
        >
          <span
            id="last28DaysButton"
            class="text-fg-link-primary cursor-pointer inline-block"
            @click="setDateFilterType('last-28-days')"
            >Last 28 Days</span
          >
          <span
            id="next28DaysButton"
            class="text-fg-link-primary cursor-pointer inline-block mx-8"
            @click="setDateFilterType('next-28-days')"
            >Next 28 Days</span
          >
          <ButtonDefault
            id="submitCustomDateRangeButton"
            label="Submit"
            classes="ml-auto"
            @click="setDateFilterType('custom')"
          />
        </div>
      </div>
    </OnClickOutside>
    <!-- Search -->
    <TextInput
      id="reportsTableSearchInput"
      :modelValue="searchQuery"
      placeholder="Search"
      @update:modelValue="onSearch"
      class="py-0"
    />
  </div>
</template>

<script lang="ts">
import { OnClickOutside } from '@vueuse/components'
import moment from 'moment'
import { defineComponent } from 'vue'

import ChevronDown from '@/assets/chevronDown.svg?component'
import ButtonDefault from '@/common/components/atoms/ButtonDefault.vue'
import SlotButton from '@/common/components/atoms/SlotButton.vue'
import TextInput from '@/common/components/molecules/TextInput.vue'
import DatePicker from '@/common/components/organisms/DatePicker.vue'
import { cleanString } from '@/utils/utils'

type DateFilterType = 'last-28-days' | 'next-28-days' | 'custom'
type DateFilter = {
  startDate: string
  endDate: string
}

export default defineComponent({
  name: 'ReportsTableFilter',
  components: {
    OnClickOutside,
    SlotButton,
    ButtonDefault,
    DatePicker,
    TextInput,
    ChevronDown,
  },
  emits: {
    // eslint-disable-next-line no-unused-vars
    filterByDateRange: (dateFilter: DateFilter) => true,
    // eslint-disable-next-line no-unused-vars
    search: (searchQuery: string) => true,
  },
  data() {
    return {
      searchQuery: '',
      isDateRangeModalOpen: false,
      startDate: moment(new Date()).format('YYYY-MM-DD'),
      endDate: moment(new Date()).add(1, 'days').format('YYYY-MM-DD'),
      dateFilterType: 'next-28-days' as DateFilterType,
    }
  },
  computed: {
    dateFilterButtonText(): string {
      if (this.dateFilterType === 'last-28-days') {
        return 'Last 28 Days'
      } else if (this.dateFilterType === 'next-28-days') {
        return 'Next 28 Days'
      } else if (this.dateFilterType === 'custom') {
        return `${moment(this.startDate).format('MM/DD/YYYY')} - ${moment(this.endDate).format(
          'MM/DD/YYYY',
        )}`
      } else {
        return 'Date Filter'
      }
    },
    isDateRangeCorrect(): boolean {
      return moment(this.startDate).isBefore(this.endDate)
    },
    isDateRangeEqual(): boolean {
      return moment(this.startDate).isSame(this.endDate)
    },
    dateRangeErrorMessage(): string {
      if (this.isDateRangeEqual) {
        return 'Start and end date cannot be the same'
      } else if (!this.isDateRangeCorrect) {
        return 'Start date cannot be after end date'
      } else {
        return ''
      }
    },
    dateFilter(): DateFilter {
      switch (this.dateFilterType) {
        case 'last-28-days': {
          const yesterday = moment(new Date()).subtract(1, 'days').format('YYYY-MM-DD')
          return {
            startDate: moment(yesterday).subtract(28, 'days').format('YYYY-MM-DD'),
            endDate: yesterday,
          }
        }
        case 'next-28-days': {
          const today = moment(new Date()).format('YYYY-MM-DD')
          return {
            startDate: today,
            endDate: moment(today).add(28, 'days').format('YYYY-MM-DD'),
          }
        }
        case 'custom':
          return {
            startDate: this.startDate,
            endDate: this.endDate,
          }
        default:
          return {
            startDate: '',
            endDate: '',
          }
      }
    },
  },
  mounted() {
    // @ts-ignore
    this.setDateFilterType(this.dateFilterType)
  },
  methods: {
    setDateFilterType(dateFilterType: DateFilterType) {
      if (dateFilterType === 'custom' && this.dateRangeErrorMessage) return
      this.dateFilterType = dateFilterType
      this.isDateRangeModalOpen = false
      this.$emit('filterByDateRange', this.dateFilter)
    },
    onSearch(query: string) {
      this.searchQuery = query
      this.$emit('search', cleanString(this.searchQuery))
    },
  },
})
</script>
